// feature-module/class/components/TabSubjectClass.tsx
import React, { useMemo } from "react";
import Datatable from "components/Common/dataTable";
import CustomButton from "components/Common/ui/CustomButton";
import { Tag } from "antd";
import { useTranslation } from "react-i18next";
import { getSubjectLabelFromCode } from "utils/schoolUtils";
import useFetchTeachers from "hooks/useFetchTeachers";
import AssignTeacherToClassModal from "./AssignTeacherToClassModal";
import useClassManagement from "../hooks/useClassManagement";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { useAbility } from "config/abilityContext";

function dedupeByCodeAndParent(list: any[]) {
    const seen = new Set<string>();
    const out: any[] = [];
    for (const s of list || []) {
        const key = `${s.code}__${s.parent?.id || "no_parent"}`;
        if (!seen.has(key)) {
            seen.add(key);
            out.push(s);
        }
    }
    return out;
}

function groupByParentSubject(items: any[]) {
    const map: Record<string, { parentId: string; parent: any | null; items: any[] }> = {};
    for (const s of items || []) {
        const p = s.parent ?? null;
        const key = p?.id ? String(p.id) : "no_parent";
        if (!map[key]) map[key] = { parentId: key, parent: p, items: [] };
        map[key].items.push(s);
    }
    return Object.values(map).sort((a, b) => {
        if (a.parentId === "no_parent" && b.parentId !== "no_parent") return -1;
        if (a.parentId !== "no_parent" && b.parentId === "no_parent") return 1;
        return (a.parent?.name || "").localeCompare(b.parent?.name || "");
    });
}

const getSubjectsModal = (record: any) => {
    const extractedSubjects =
        record?.map((cs: any) => ({
            id: cs.id,
            name: cs.subject?.name || "unknown_subject",
            coefficient: cs.coefficient,
            colorCode: cs.subject?.colorCode,
            code: cs.subject?.code,
            parent: cs.subject?.parent ?? null,
            teacher: cs.teacher ?? null,
        })) || [];

    return extractedSubjects;
};

interface Props {
    subjects: any[];
    loading?: boolean;
    pagination?: boolean;
    reloadDetailClass?: () => void;
}

/** ---------- component ---------- */
const TabSubjectClass: React.FC<Props> = ({
    subjects,
    loading = false,
    pagination = false,
    reloadDetailClass,
}) => {
    const { t } = useTranslation();
    const { teachers } = useFetchTeachers();
    const [openAssignTeacherModal, setOpenAssignTeacherModal] = React.useState(false);
    const [selectedClassSubject, setSelectedClassSubject] = React.useState<any>(null);
    const onAssignTeacher = (row: any) => {
        setOpenAssignTeacherModal(true);
        setSelectedClassSubject(row);
    };
    const ability = useAbility();
    const { handleAssignTeacherToClass } = useClassManagement();

    const handleAssignTeacher = async (teacherId: string) => {
        await handleAssignTeacherToClass(selectedClassSubject.id, teacherId)
        reloadDetailClass?.()
        setSelectedClassSubject(null)
    };
    const teacherOptions = teachers?.map((t: any) => ({
        value: t.id,
        label: t.user.firstName + " " + t.user.lastName,
    })) || [];


    const dataSource: any[] = useMemo(() => {
        const clean = dedupeByCodeAndParent(getSubjectsModal(subjects) || []);
        const groups = groupByParentSubject(clean);

        const rows: any[] = [];
        for (const g of groups) {
            const label =
                g.parentId === "no_parent"
                    ? t("base_subjects")
                    : getSubjectLabelFromCode(g.parent?.code, t) || g.parent?.name || t("subject");

            rows.push({
                key: `group_${g.parentId}`,
                rowType: "group",
                groupLabel: label,
            });

            for (const s of g.items) {
                rows.push({
                    key: `${s.code}_${s.id ?? Math.random()}`,
                    rowType: "subject",
                    code: s.code,
                    name: s.name,
                    coefficient: s.coefficient,
                    colorCode: s.colorCode,
                    teacher: s.teacher || null,
                    raw: s,
                });
            }
        }
        return rows;
    }, [subjects, t]);

    const columns = [
        {
            title: t("subject"),
            dataIndex: "name",
            key: "subject",
            render: (_: any, row: any, __: number) => {
                if (row.rowType === "group") {
                    return (
                        <div className="py-2">
                            <span className="text-uppercase fw-bold small text-dark">{row.groupLabel}</span>
                        </div>
                    );
                }
                return (
                    <div className="d-flex align-items-center">
                        <span
                            className="me-2"
                            style={{
                                display: "inline-block",
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: row.colorCode || "#ccc",
                            }}
                        />
                        <span className="fw-semibold">
                            {getSubjectLabelFromCode(row.code, t) || row.name || row.code}
                        </span>
                    </div>
                );
            },
            onCell: (row: any) =>
                row.rowType === "group" ? { colSpan: 4 } : {},
        },
        {
            title: t("coefficient"),
            dataIndex: "coefficient",
            key: "coefficient",
            width: 140,
            render: (v: any, row: any) => (row.rowType === "group" ? null : <span className="badge rounded-pill bg-primary">{t("coefficient")}: {v ?? "-"}</span>),
            onCell: (row: any) => (row.rowType === "group" ? { colSpan: 0 } : {}),
        },
        {
            title: t("teachers"),
            key: "teachers",
            render: (_: any, row: any) => {
                if (row.rowType === "group") return null;
                if (row?.teacher) {
                    return <UserProfileHeader
                        firstName={row?.teacher?.user?.firstName}
                        lastName={row?.teacher?.user?.lastName}
                        photo={row?.teacher?.user?.photo}

                    />
                }
                return <Tag key={row.id}>{t("no_teacher_assigned")}</Tag>

            },
            onCell: (row: any) => (row.rowType === "group" ? { colSpan: 0 } : {}),
        },
        {
            title: t("actions"),
            key: "actions",
            width: 170,
            render: (_: any, row: any) =>
                row.rowType === "group" ? null : (
                    <CustomButton
                        onClick={() => onAssignTeacher?.(row.raw)}
                        variant="primary"
                        disabled={!ability.can("update", "Class")}
                    >
                        <i className="bi bi-person-plus-fill"></i>
                        {t("assign_teacher")}
                    </CustomButton>
                ),
            onCell: (row: any) => (row.rowType === "group" ? { colSpan: 0 } : {}),
        },
    ];

    return (
        <>
            <Datatable
                columns={columns as any}
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                bordered={false}
                rowKey="key"
            />

            <AssignTeacherToClassModal
                open={openAssignTeacherModal}
                onClose={() => setOpenAssignTeacherModal(false)}
                onSave={handleAssignTeacher}
                teacherOptions={teacherOptions}
                selectedClassSubject={selectedClassSubject}
            />
        </>

    );
};

export default TabSubjectClass;
